import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import {
  Area,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ComposedChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  Bookmark,
  ChevronDownIcon,
  ChevronLeft,
  ChevronRight,
  CircleCheckBig,
  Clock,
  HelpCircle,
  Mic,
  Pause,
  Play,
  Plus,
  ShieldCheck,
  TriangleAlert,
  Volume2,
} from "lucide-react";
import * as z from "zod";
import {
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import * as duration from "duration-fns";
import { addMilliseconds, differenceInMilliseconds } from "date-fns";
import { useInView } from "react-intersection-observer";
import { PiiListTab } from "../../../participant-details/-components/PiiListTab";
import {
  extractAiDetectionResults,
  extractPiiResults,
  extractTranscriptionWords,
} from "../../-hooks/utterance/transcription";
import { extractVexonResult } from "../../-hooks/utterance/vexon";
import type { ChartConfig } from "@/components/ui/chart";
import type { UtteranceHistoryEntry } from "@/lib/queries/utterances";
import type { Utterance } from "../../-hooks/meeting";
import {
  useBookmark,
  utteranceHistoryInfiniteQueryOptions,
} from "@/lib/queries/utterances";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ConfirmationDialog,
  ConfirmationDialogWarningIcon,
} from "@/components/ui/confirmation-dialog";
import { cn } from "@/lib/utils";
import {
  participantAnalysisTimelineQueryOptions,
  participantDetailQueryOptions,
} from "@/lib/queries/participants";
import { meetingBotQueryOptions } from "@/lib/queries/meetings";
import {
  formatDuration,
  formatMeetingTimestamp,
  formatUtteranceDurationShort,
  normalizedDifferenceDuration,
} from "@/lib/utils/duration";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { invariant } from "@/lib/invariant";
import { useIsMobile } from "@/lib/hooks/mediaQuery";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import NoSegmentIllustration from "@/data/images/no-segment-illustration.svg";
import {
  UtteranceAnalysisTabBar,
  UtteranceAnalysisTabContent,
  useUtteranceAnalysisTabs,
} from "@/components/utterances";

const paramsSchema = z.object({
  meetingId: z.uuid(),
  participantId: z.uuid(),
});

export const Route = createFileRoute(
  "/_app/meetings/$meetingId/participant-details/$participantId",
)({
  params: { parse: paramsSchema.parse },
  component: RouteComponent,
  pendingMs: 100,
  async loader({
    context: { queryClient },
    params: { participantId, meetingId },
  }) {
    queryClient.ensureQueryData(
      participantAnalysisTimelineQueryOptions(participantId),
    );
    const [meetingBot] = await Promise.all([
      queryClient.ensureQueryData(meetingBotQueryOptions(meetingId)),
      queryClient.ensureQueryData(participantDetailQueryOptions(participantId)),
      queryClient.ensureInfiniteQueryData(
        utteranceHistoryInfiniteQueryOptions(meetingId, {
          participant_id: participantId,
        }),
      ),
    ]);

    if (meetingBot.state_group !== "ENDED")
      throw redirect({
        to: "/meetings/$meetingId",
        params: {
          meetingId: meetingBot.id,
        },
      });
  },
});

const SelectedUtteranceContext = createContext<
  [
    UtteranceHistoryEntry | null,
    React.Dispatch<React.SetStateAction<UtteranceHistoryEntry | null>>,
  ]
>(null!);

function useSelectedUtterance() {
  return useContext(SelectedUtteranceContext);
}

function useMeeting() {
  const meetingId = Route.useParams({
    select: (p) => p.meetingId,
  });
  return useSuspenseQuery(meetingBotQueryOptions(meetingId));
}
function useParticipant() {
  const participantId = Route.useParams({
    select: (p) => p.participantId,
  });

  return useSuspenseQuery(participantDetailQueryOptions(participantId));
}

const SEGMENT_TABS = [
  { value: "transcript", label: "Transcription Segment" },
  { value: "pii", label: "PII" },
] as const;

type SegmentTabValue = (typeof SEGMENT_TABS)[number]["value"];

function Breadcrumbs() {
  const meeting = useMeeting();
  const participant = useParticipant();

  const crumbs = [
    { label: "My Meetings", to: "/meetings" as const },
    {
      label: meeting.data.meeting_name,
      to: "/meetings/$meetingId" as const,
      params: { meetingId: meeting.data.id },
    },
    { label: participant.data.full_name, to: null },
  ];

  return (
    <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm">
      {crumbs.map((crumb, index) => {
        return (
          <span key={crumb.label} className="flex items-center gap-1.5">
            {crumb.to ? (
              <Link
                to={crumb.to}
                params={"params" in crumb ? crumb.params : undefined}
                className="font-medium text-[#DD2590] hover:text-[#DD2590]/80"
              >
                {crumb.label}
              </Link>
            ) : (
              <span className="font-medium text-[#414651] dark:text-foreground">
                {crumb.label}
              </span>
            )}
            {!(index === crumbs.length - 1) && (
              <ChevronRight className="size-4 text-[#A4A7AE] dark:text-muted-foreground" />
            )}
          </span>
        );
      })}
    </nav>
  );
}

function MeetingMeta() {
  const participant = useParticipant();

  const spokenDuration = duration.normalize(
    {
      milliseconds: participant.data.analytics?.speech_duration_ms ?? 0,
    },
    participant.data.created_at,
  );

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
      <div className="flex items-center gap-2 text-sm text-[#414651] dark:text-foreground">
        <Clock className="size-5 text-[#717680] dark:text-muted-foreground" />
        <span>{new Date(participant.data.created_at).toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2 text-sm text-[#414651] dark:text-foreground">
        <Mic className="size-5 text-[#717680] dark:text-muted-foreground" />
        <span>{formatDuration(spokenDuration)}</span>
      </div>
    </div>
  );
}

function RiskAlertCard() {
  return (
    <Card className="gap-0 overflow-hidden border-l-4 border-l-[#F04438] py-0">
      <CardContent className="flex flex-col gap-4 p-4 sm:gap-6 sm:p-6">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <TriangleAlert className="size-5 text-[#F04438]" />
            <p className="text-base font-semibold text-[#F04438]">
              Do not advance!
            </p>
          </div>
          <p className="text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            High-confidence findings across multiple detection methods. AI
            speech detection, voice authentication failure, and PII disclosure
            all indicate this participant is not communicating authentically. Do
            not advance without re-interview under controlled conditions.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-[#0BA5EC]" />
            <p className="text-sm font-semibold text-[#181D27] dark:text-foreground">
              Recommended action
            </p>
          </div>
          <p className="text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Do not advance. Request re-interview via verified video platform
            with proctoring enabled. Candidate&apos;s responses show
            AI-generation patterns across multiple answers. Voice anomaly
            detected. Review any disclosed PII for social engineering risk.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function SafeCard() {
  return (
    <Card className="gap-0 overflow-hidden border-l-4 border-l-[#079455] py-0">
      <CardContent className="flex flex-col gap-4 p-4 sm:gap-6 sm:p-6">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <CircleCheckBig className="size-5 text-[#079455]" />
            <p className="text-base font-semibold text-[#079455]">
              No Action Required
            </p>
          </div>
          <p className="max-w-4xl text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Voice analysis, AI speech detection, and PII scan all returned clean
            results. This participant&apos;s session can be used confidently in
            your evaluation process.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-[#0BA5EC]" />
            <p className="text-sm font-semibold text-[#181D27] dark:text-foreground">
              Recommended action
            </p>
          </div>
          <p className="max-w-4xl text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Clear this participant and proceed with the standard evaluation
            workflow.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ModerateCard() {
  return (
    <Card className="gap-0 overflow-hidden border-l-4 border-l-[#F79009] py-0">
      <CardContent className="flex flex-col gap-4 p-4 sm:gap-6 sm:p-6">
        <div className="space-y-2.5">
          <div className="flex items-center gap-2">
            <TriangleAlert className="size-5 text-[#F79009]" />
            <p className="text-base font-semibold text-[#F79009]">
              Review Recommended
            </p>
          </div>
          <p className="max-w-4xl text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Moderate AI speech probability and voice anomalies detected.
            Consider a follow-up conversation or additional identity
            verification before making a final decision.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-[#0BA5EC]" />
            <p className="text-sm font-semibold text-[#181D27] dark:text-foreground">
              Recommended action
            </p>
          </div>
          <p className="max-w-4xl text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Schedule a follow-up session. Confirm identity through an
            independent channel. Review flagged transcript segments manually.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCards() {
  const participant = useParticipant();

  const { analytics } = participant.data;

  // TODO: fill empty space when not all detectors are active
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 sm:gap-6">
      <TooltipProvider>
        <Card className="group relative h-full overflow-hidden border">
          <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] select-none dark:text-white/6">
            AI Speech
          </span>
          <CardHeader className="relative z-10 pb-2">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-normal text-[#DD2590]">
                {analytics?.risk_score.ai_component != null
                  ? Math.round(analytics.risk_score.ai_component)
                  : "--"}
                %
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {/* <p className="mb-1 min-h-10 text-xs leading-4 text-muted-foreground">
            {stat.caption ?? ""}
          </p> */}
            <p className="text-lg font-normal text-[#414651] dark:text-white">
              AI Speech
            </p>
          </CardContent>
        </Card>

        <Card className="group relative h-full overflow-hidden border">
          <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] select-none dark:text-white/6">
            PII Detected
          </span>
          <CardHeader className="relative z-10 pb-2">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-normal text-[#DD2590]">
                {/* TODO: this might be nullable if pii detection is disabled in features, have to check */}
                {(analytics?.pii.count as number | null | undefined) ?? "--"}
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {/* <p className="mb-1 min-h-10 text-xs leading-4 text-muted-foreground">
            {stat.caption ?? ""}
          </p> */}
            <p className="text-lg font-normal text-[#414651] dark:text-white">
              PII
            </p>
          </CardContent>
        </Card>

        <Card className="group relative h-full overflow-hidden border">
          <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] select-none dark:text-white/6">
            Voice Authenticity
          </span>
          {/* {stat.hasInfo && (
           <HelpCircle className="absolute top-4 right-4 z-20 size-4 text-[#A4A7AE]" />
          )} */}
          <CardHeader className="relative z-10 pb-2">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-normal text-[#DD2590]">
                {analytics?.risk_score.vexon_component != null
                  ? Math.round(analytics.risk_score.vexon_component)
                  : "--"}
                %
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {/* <p className="mb-1 min-h-10 text-xs leading-4 text-muted-foreground">
            {stat.caption ?? ""}
          </p> */}
            <p className="text-lg font-normal text-[#414651] dark:text-white">
              Voice Authenticity
            </p>
          </CardContent>
        </Card>

        <Card className="group relative h-full overflow-hidden border">
          <span className="pointer-events-none absolute bottom-2 -left-5 text-5xl font-black text-[#FAFAFA] select-none dark:text-white/6">
            Risk Score
          </span>
          <Tooltip>
            <TooltipTrigger className="absolute top-4 right-4 z-20">
              <HelpCircle className="size-4 text-[#A4A7AE] dark:text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              The risk score is calculated as an average of all analysis types.
            </TooltipContent>
          </Tooltip>

          <CardHeader className="relative z-10 pb-2">
            <div className="flex items-center justify-between">
              <div className="text-3xl font-normal text-[#DD2590]">
                {analytics?.risk_score.total == null
                  ? "--"
                  : Math.round(analytics.risk_score.total)}
                {/* /100 */}
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative z-10">
            {/* <p className="mb-1 min-h-10 text-xs leading-4 text-muted-foreground">
            {stat.caption ?? ""}
          </p> */}
            <p className="text-lg font-normal text-[#414651] dark:text-white">
              Risk Score
            </p>
          </CardContent>
        </Card>
      </TooltipProvider>
    </div>
  );
}

const chartConfig = {
  aiSpeech: { label: "AI Speech", color: "#7A5AF8" },
  vexon: { label: "Voice Deepfake", color: "#0BA5EC" },
} satisfies ChartConfig;

type SeriesKey = keyof typeof chartConfig;

const CHIP_ACTIVE_CLASSES: Record<SeriesKey, string> = {
  aiSpeech:
    "border-[#D9D6FE] bg-[#F4F3FF] text-[#5925DC] dark:border-[#7A5AF8]/40 dark:bg-[#7A5AF8]/15 dark:text-[#B9A7FB]",
  vexon:
    "border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2] dark:border-[#0BA5EC]/40 dark:bg-[#0BA5EC]/15 dark:text-[#7CD4FD]",
};
function AnalysisGraph() {
  const participantId = Route.useParams({
    select: (s) => s.participantId,
  });

  const meeting = useMeeting();

  const start =
    meeting.data.meeting_start_time ||
    meeting.data.recording?.start_time ||
    meeting.data.join_at;
  invariant(start !== null, "No start time could be found");
  const startTime = new Date(start);
  const endMs = differenceInMilliseconds(
    meeting.data.left_at ?? startTime,
    startTime,
  );

  const formatTime = useCallback(
    (milliseconds: number) => {
      const dur = duration.normalize({ milliseconds }, startTime);

      return formatMeetingTimestamp(dur);
    },
    [meeting.data],
  );

  const participantTimeline = useQuery({
    ...participantAnalysisTimelineQueryOptions(participantId),
    select(data) {
      const chartData: Array<{
        timeMs: number;
        aiSpeech: number | null;
        vexon: number | null;
      }> = [];

      chartData.push({
        timeMs: 0,
        aiSpeech: 0,
        vexon: 0,
      });

      data.ai_content_detection.forEach((e) =>
        chartData.push({
          timeMs: e.start_time_ms,
          aiSpeech: e.score * 100,

          vexon: null,
        }),
      );

      data.voice_authentication.forEach((e) =>
        chartData.push({
          timeMs: e.start_time_ms,
          aiSpeech: null,
          vexon: e.fake_prob * 100,
        }),
      );
      chartData.push({
        timeMs: endMs,
        aiSpeech: 0,
        vexon: 0,
      });

      chartData.sort((a, b) => a.timeMs - b.timeMs);

      return chartData;
    },
  });

  const [series, setSeries] = useState<Record<SeriesKey, boolean>>({
    aiSpeech: true,
    vexon: true,
  });

  const toggle = (key: SeriesKey) =>
    setSeries((prev) => ({ ...prev, [key]: !prev[key] }));

  const { current: gradientPrefix } = useRef(
    `graphFill-${crypto.randomUUID()}`,
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="p-4 pb-2 sm:p-6 sm:pb-2">
        <div className="flex items-center gap-1.5">
          <CardTitle className="text-base font-semibold sm:text-lg">
            Analysis Graph
          </CardTitle>
          <HelpCircle className="size-4 text-[#A4A7AE] dark:text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent className="p-2 pt-4 sm:p-6 sm:pt-4">
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <ComposedChart
            data={participantTimeline.data}
            margin={{ top: 8, right: 4, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient
                id={gradientPrefix + "-ai"}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={chartConfig.aiSpeech.color}
                  stopOpacity={0.22}
                />
                <stop
                  offset="100%"
                  stopColor={chartConfig.aiSpeech.color}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <defs>
              <linearGradient
                id={gradientPrefix + "-vexon"}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={chartConfig.vexon.color}
                  stopOpacity={0.22}
                />
                <stop
                  offset="100%"
                  stopColor={chartConfig.vexon.color}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={false}
              vertical={false}
            />
            <XAxis
              dataKey="timeMs"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#A4A7AE" }}
              tickFormatter={formatTime}
              dy={8}
              domain={!meeting.data.left_at ? undefined : [0, endMs]}
              padding={{
                left: 35,
                right: 10,
              }}
            />
            <YAxis hide domain={[0, 100]} />
            <ChartTooltip
              content={<ChartTooltipContent labelKey="minute" />}
              cursor={{ stroke: "#E9EAEB" }}
            />
            {/* <ReferenceLine
              x={2}
              stroke="#DD2590"
              strokeWidth={1.5}
              ifOverflow="extendDomain"
            /> */}

            <Area
              hide={!series.aiSpeech}
              connectNulls
              baseValue={0}
              type="monotone"
              dataKey="aiSpeech"
              stroke="var(--color-aiSpeech)"
              strokeWidth={2}
              fill={`url(#${gradientPrefix}-ai)`}
              dot={false}
              activeDot={false}
            />

            <Area
              hide={!series.vexon}
              connectNulls
              baseLine={0}
              type="monotone"
              dataKey="vexon"
              stroke="var(--color-vexon)"
              fill={`url(#${gradientPrefix}-vexon)`}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ChartContainer>

        <p className="mt-1 text-center text-xs text-[#717680] dark:text-muted-foreground">
          Timeline
        </p>

        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          {(Object.keys(chartConfig) as Array<SeriesKey>).map((key) => {
            const active = series[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                className={cn(
                  "inline-flex items-center rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? CHIP_ACTIVE_CLASSES[key]
                    : "border-[#E9EAEB] bg-white text-[#717680] dark:border-border dark:bg-card dark:text-muted-foreground",
                )}
              >
                {chartConfig[key].label}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// TODO: replace this with <Tabs/> component
function SegmentTabBar({
  active,
  onChange,
}: {
  active: SegmentTabValue;
  onChange: (value: SegmentTabValue) => void;
}) {
  return (
    <div className="rounded-lg border border-[#E9EAEB] bg-[#FAFAFA] p-1 dark:border-border dark:bg-muted/40">
      <div className="grid grid-cols-2 gap-1">
        {SEGMENT_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={cn(
              "rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
              active === tab.value
                ? "bg-white text-[#181D27] shadow-xs dark:bg-card dark:text-foreground"
                : "text-[#717680] hover:text-[#414651] dark:text-muted-foreground dark:hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SegmentNoteEditor({
  value,
  onChange,
  onCancel,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <label
          htmlFor="segment-note-input"
          className="text-sm font-medium text-[#414651] dark:text-foreground"
        >
          Note
        </label>
        <Textarea
          id="segment-note-input"
          placeholder="Add a note for this section..."
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-24 resize-y rounded-lg border-[#E9EAEB] bg-white text-sm placeholder:text-[#A4A7AE] focus-visible:border-[#DD2590] focus-visible:ring-[#DD2590]/20 dark:border-border dark:bg-input/30 dark:placeholder:text-muted-foreground"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="rounded-lg border-[#D5D7DA] bg-white text-[#414651] hover:bg-[#FAFAFA] dark:border-border dark:bg-input/30 dark:text-white dark:hover:bg-muted/40 dark:hover:text-white"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!value.trim()}
          className="rounded-lg bg-[#DD2590] px-5 text-sm font-semibold text-white hover:bg-[#C11574] disabled:opacity-50"
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function TranscriptSegmentsSkeleton({ itemCount = 2 }: { itemCount?: number }) {
  return (
    <div className="space-y-3" aria-label="Loading transcription segments">
      {["first", "second"].slice(0, itemCount).map((item) => (
        <div
          key={item}
          className="rounded-xl border border-[#E9EAEB] bg-[#FAFAFA] p-3 dark:border-border dark:bg-muted/40"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-5 w-14" />
              </div>
              <div className="mt-3 space-y-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-11/12" />
                <Skeleton className="h-5 w-3/4" />
              </div>
            </div>
            <Skeleton className="size-6 shrink-0" />
          </div>
          <Skeleton className="mt-3 h-5 w-20" />
        </div>
      ))}
    </div>
  );
}

function TranscriptSegments({ showSavedOnly }: { showSavedOnly: boolean }) {
  const meeting = useMeeting();
  const participant = useParticipant();
  const utteranceHistory = useInfiniteQuery(
    utteranceHistoryInfiniteQueryOptions(meeting.data.id, {
      participant_id: participant.data.id,
      has_bookmark: showSavedOnly || undefined,
    }),
  );

  const loadedUtterancesCount = useMemo(
    () => utteranceHistory.data?.pages.flatMap((p) => p.items).length ?? 0,
    [utteranceHistory.data],
  );
  const triggerIdx = loadedUtterancesCount - 1;
  const triggerTreshold = 4;

  if (
    utteranceHistory.isPending ||
    (utteranceHistory.isFetching && !utteranceHistory.isFetchingNextPage)
  )
    return <TranscriptSegmentsSkeleton />;

  return (
    <div className="space-y-3">
      {utteranceHistory.data?.pages.flatMap((page, pageIdx) =>
        page.items.map((item, itemIdx) => {
          const idx = pageIdx * page.items.length + itemIdx;
          return (
            <TranscriptUtterance
              key={item.id}
              utterance={item}
              onIntersectStateChange={
                Math.abs(idx - triggerIdx) < triggerTreshold
                  ? (intersecting: boolean) => {
                      if (intersecting) utteranceHistory.fetchNextPage();
                    }
                  : undefined
              }
            />
          );
        }),
      )}
      {utteranceHistory.isFetchingNextPage && (
        <TranscriptSegmentsSkeleton itemCount={1} />
      )}
    </div>
  );
}

function TranscriptUtterance({
  utterance,
  onIntersectStateChange,
}: {
  utterance: UtteranceHistoryEntry;
  onIntersectStateChange?: (state: boolean) => void;
}) {
  const [selectedUtterance, setSelectedUtterance] = useSelectedUtterance();
  const isSelected = selectedUtterance?.id === utterance.id;

  const [noteDraft, setNoteDraft] = useState<string | null>(null);

  const bookmark = useBookmark(utterance.id);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const { ref } = useInView({
    onChange: onIntersectStateChange,
    skip: !onIntersectStateChange,
  });

  const timestamp = duration.normalize(
    {
      milliseconds: utterance.start_time_ms,
    },
    utterance.start_time,
  );

  return (
    <div
      ref={ref}
      className={cn(
        "cursor-pointer rounded-xl border bg-[#FAFAFA] p-3 transition-colors dark:bg-muted/40",
        isSelected
          ? "border-[#DD2590] bg-white dark:bg-card"
          : "border-[#E9EAEB] hover:border-[#D5D7DA] dark:border-border dark:hover:border-input",
      )}
      onClick={() =>
        isSelected
          ? setSelectedUtterance(null)
          : setSelectedUtterance(utterance)
      }
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-[#181D27] dark:text-foreground">
              {formatMeetingTimestamp(timestamp)}
            </span>
            <span className="text-sm text-[#717680] dark:text-muted-foreground">
              {formatUtteranceDurationShort(
                duration.normalize({ milliseconds: utterance.duration_ms }),
              )}
            </span>
          </div>
          <p className="mt-2 line-clamp-3 text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            {utterance.transcription_text}
          </p>
        </div>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            bookmark.detail.data
              ? bookmark.detail.data.note
                ? setOpenDeleteConfirmation(true)
                : bookmark.delete.mutate()
              : bookmark.create.mutate(null);
          }}
          aria-label={bookmark.detail.data ? "Unsave segment" : "Save segment"}
          className="shrink-0 cursor-pointer rounded-md p-1 text-[#717680] transition-colors hover:bg-[#F5F5F5] hover:text-[#414651] dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground"
        >
          <Bookmark
            className={cn(
              "size-4",
              bookmark.detail.data
                ? "fill-[#414651] text-[#414651] dark:fill-foreground dark:text-foreground"
                : "text-[#A4A7AE] dark:text-muted-foreground",
            )}
          />
        </button>
        <ConfirmationDialog
          open={openDeleteConfirmation}
          onOpenChange={setOpenDeleteConfirmation}
          title="Unsave?"
          description="Unsaving this segment will also permanently delete your attached note."
          onConfirm={bookmark.delete.mutate}
          confirmText="Unsave"
          cancelText="Cancel"
          icon={ConfirmationDialogWarningIcon}
          backgroundDecoration
        />
      </div>

      {noteDraft !== null ? (
        <SegmentNoteEditor
          value={noteDraft}
          onChange={setNoteDraft}
          onCancel={() => setNoteDraft(null)}
          onSubmit={() =>
            (bookmark.detail.data ? bookmark.update : bookmark.create).mutate(
              noteDraft.trim(),
              {
                onSettled: () => setNoteDraft(null),
              },
            )
          }
        />
      ) : (
        <div className="mt-2">
          {bookmark.detail.data?.note ? (
            <Collapsible>
              <CollapsibleTrigger
                onClick={(event) => event.stopPropagation()}
                className="group/collapsible inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-[#DD2590] hover:text-[#C11574]"
              >
                View Note
                <ChevronDownIcon className="group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="m-2">
                <Button onClick={() => setNoteDraft("")}>Edit</Button>
                <Card className="mt-2 p-4">{bookmark.detail.data.note}</Card>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setNoteDraft("");
              }}
              className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-[#DD2590] hover:text-[#C11574]"
            >
              <Plus className="size-4" />
              Add Note
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function pseudoRandom(index: number): number {
  const value = Math.sin(index * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
}

// @ts-expect-error will reimplement and use it soon
function CompactVoiceWaveform({ fakeRatio }: { fakeRatio: number }) {
  const barCount = 120;
  const splitIndex = Math.round(barCount * fakeRatio);

  return (
    <div className="flex h-12 items-end gap-px overflow-hidden rounded-md">
      {Array.from({ length: barCount }, (_, index) => {
        const height = 8 + pseudoRandom(index) * 32;
        const isFake = index < splitIndex;
        return (
          <span
            key={index}
            className="w-0.5 shrink-0 rounded-full"
            style={{
              height: `${height}px`,
              backgroundColor: isFake ? "#F04438" : "#17B26A",
              opacity: 0.85,
            }}
          />
        );
      })}
    </div>
  );
}

function AudioPlaybackControls({
  currentTime,
  duration: audioDuration,
  isPlaying,
  onTogglePlay,
  onSeek,
}: {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onSeek: (value: number) => void;
}) {
  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  const formatTime = (seconds: number) => {
    return formatMeetingTimestamp(
      duration.normalize({ seconds: Math.round(seconds) }),
    );
  };

  const formatDurationLabel = (seconds: number) => {
    const roundedSeconds = Math.round(seconds);
    const mins = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    if (mins > 0) return `${mins}m ${secs} secs`;
    return `${secs} secs`;
  };

  return (
    <div className="space-y-3">
      <div className="relative h-1.5 rounded-full bg-[#E9EAEB] dark:bg-muted">
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-[#717680]"
          style={{ width: `${progress}%` }}
        />
        <input
          type="range"
          min={0}
          max={audioDuration}
          step={0.1}
          value={currentTime}
          onChange={(event) => onSeek(Number(event.target.value))}
          aria-label="Audio progress"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <span
          className="pointer-events-none absolute top-1/2 size-3 -translate-y-1/2 rounded-full border-2 border-white bg-[#717680] shadow-sm"
          style={{ left: `calc(${progress}% - 6px)` }}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-xs text-[#717680] dark:text-muted-foreground">
          {formatTime(currentTime)} ({formatDurationLabel(audioDuration)})
        </span>
        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
          className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#E9EAEB] bg-white text-[#717680] transition-colors hover:bg-[#FAFAFA] dark:border-border dark:bg-card dark:text-muted-foreground dark:hover:bg-muted/40"
        >
          {isPlaying ? (
            <Pause className="size-3.5 fill-current" />
          ) : (
            <Play className="size-3.5 fill-current" />
          )}
        </button>
        <span className="shrink-0 text-xs text-[#717680] dark:text-muted-foreground">
          {formatTime(audioDuration)}
        </span>
      </div>
    </div>
  );
}

function AnalysisDetailsContent({
  utterance,
  isPlaying,
  onTogglePlay,
  currentTime,
  onSeek,
  durationSeconds,
}: {
  utterance: UtteranceHistoryEntry;
  isPlaying: boolean;
  onTogglePlay: () => void;
  currentTime: number;
  onSeek: (value: number) => void;
  durationSeconds: number;
}) {
  const meeting = useMeeting();
  const enabledFeatures = new Set<
    "ai_content_detection" | "voice_authentication" | "pii"
  >(
    meeting.data.features.map(
      (f) => f.label as "ai_content_detection" | "voice_authentication" | "pii",
    ),
  );
  const start =
    meeting.data.meeting_start_time ||
    meeting.data.recording?.start_time ||
    meeting.data.join_at;
  invariant(start !== null, "No start time could be found");
  const startTime = new Date(start);

  const parsedUtterance: Utterance = {
    id: utterance.id,
    speaker: {
      id: utterance.participant.id,
      name: utterance.participant.full_name,
    },

    start: normalizedDifferenceDuration(startTime, utterance.start_time),
    end: normalizedDifferenceDuration(
      startTime,
      addMilliseconds(utterance.start_time, utterance.duration_ms),
    ),
    duration: duration.normalize(
      {
        milliseconds: utterance.duration_ms,
      },
      start,
    ),

    text: utterance.transcription_text,
    transcriptionWords: extractTranscriptionWords(
      utterance.transcription_words,
      startTime,
      new Date(utterance.start_time),
    ),

    ai_detection: extractAiDetectionResults(
      utterance.ai_results.ai_content_detection,
    ),
    pii: extractPiiResults(utterance.ai_results.pii),
    vexon: extractVexonResult(utterance.ai_results.voice_authentication),
  };

  const { tabs, setActiveTab, activeTab } = useUtteranceAnalysisTabs(
    parsedUtterance,
    enabledFeatures.difference(new Set(["voice_authentication"])), // pretend that vexon doesn't exist
  );

  const timestamp = duration.normalize(
    {
      milliseconds: utterance.start_time_ms,
    },
    utterance.start_time,
  );

  const durationTimestamp = duration.normalize(
    {
      milliseconds: utterance.duration_ms,
    },
    utterance.start_time,
  );

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-[#181D27] dark:text-foreground">
              Analysis Details
            </h3>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-xl font-semibold text-[#181D27] dark:text-foreground">
            {formatMeetingTimestamp(timestamp)}
          </p>
          <p className="text-sm text-[#717680] dark:text-muted-foreground">
            {formatDuration(durationTimestamp)}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-[#414651] dark:text-foreground">
            Voice Waveform
          </p>
          <Badge
            variant="outline"
            className="gap-1 rounded-full border-[#ABEFC6] bg-[#ECFDF3] px-2 py-0.5 text-xs font-medium text-[#067647] dark:border-green-800 dark:bg-green-950/40 dark:text-green-400"
          >
            <Volume2 className="size-3" />
            {utterance.ai_results.voice_authentication?.result_type ===
            "success" ? (
              <span>
                {Math.round(
                  utterance.ai_results.voice_authentication.overall_score
                    .authenticity.fake_ratio * 100,
                )}
                %
              </span>
            ) : (
              "no voice authenticity data"
            )}
          </Badge>
        </div>

        {/* didn't have enough time to implement real waveform */}
        {/* <CompactVoiceWaveform fakeRatio={fakeRatio} /> */}

        <AudioPlaybackControls
          currentTime={currentTime}
          duration={durationSeconds}
          isPlaying={isPlaying}
          onTogglePlay={onTogglePlay}
          onSeek={onSeek}
        />
      </div>
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* TODO: this is temporarily shoehorning this data into a component meant for a different page, need to make it more generic to work in both pages properly */}
          <UtteranceAnalysisTabBar
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </div>
      </div>

      <UtteranceAnalysisTabContent
        activeTab={activeTab}
        utterance={parsedUtterance}
        enabledFeatures={enabledFeatures}
      />

      {/* <disv className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-[#414651]">Transcript</p>
          <Badge
            variant="outline"
            className="rounded-full border-[#ABEFC6] bg-[#ECFDF3] px-2 py-0.5 text-xs font-medium text-[#067647]"
          >
            {utterance.ai_results.ai_content_detection?.result_type ===
            "success" ? (
              <span>
                {utterance.ai_results.ai_content_detection.overall_score.AI}%
              </span>
            ) : (
              "no ai detection data"
            )}
          </Badge>
          <Badge
            variant="outline"
            className="rounded-full border-[#ABEFC6] bg-[#ECFDF3] px-2 py-0.5 text-xs font-medium text-[#067647]"
          >
            {utterance.ai_results.pii?.result_type === "success" ? (
              <span>{utterance.ai_results.pii.entities.length} PIIs</span>
            ) : (
              "no pii data"
            )}
          </Badge>
        </div>

        <AnalysisDetailTranscript />
      </disv> */}
    </>
  );
}

function AnalysisDetailsPanel({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [selectedUtterance] = useSelectedUtterance();

  const isMobile = useIsMobile();

  // useEffect(() => {
  //   if (!open) return;
  //   if (!isMobile) return;

  //   const previousOverflow = document.body.style.overflow;
  //   document.body.style.overflow = "hidden";

  //   return () => {
  //     document.body.style.overflow = previousOverflow;
  //   };
  // }, [open, isMobile]);

  const toggleOpen = useCallback(() => setOpen((b) => !b), [setOpen]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    audio.load();
    setIsPlaying(false);
    setCurrentTime(0);
    setDurationSeconds(0);
  }, [selectedUtterance?.id]);

  const content =
    selectedUtterance == null || audioRef.current == null ? (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-5 py-8">
        <img
          src={NoSegmentIllustration}
          alt=""
          className="h-[100px] w-[212px] object-contain"
        />
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-lg leading-7 font-semibold text-[#181D27] dark:text-foreground">
            No segment selected
          </p>
          <p className="max-w-[294px] text-sm leading-5 text-[#535862] dark:text-muted-foreground">
            Click on any segment in the transcription segment list to see its
            analysis details here.
          </p>
        </div>
      </div>
    ) : (
      <AnalysisDetailsContent
        key={selectedUtterance.id}
        utterance={selectedUtterance}
        isPlaying={isPlaying}
        currentTime={currentTime}
        onTogglePlay={() => {
          if (isPlaying) audioRef.current?.pause();
          else audioRef.current?.play();
        }}
        onSeek={(t) => {
          if (audioRef.current) audioRef.current.currentTime = t;
        }}
        durationSeconds={durationSeconds}
      />
    );

  return (
    <>
      <audio
        ref={audioRef}
        src={selectedUtterance?.storage_path ?? undefined}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onDurationChange={() =>
          setDurationSeconds(audioRef.current?.duration ?? 0)
        }
        onLoadedData={() => setDurationSeconds(audioRef.current?.duration ?? 0)}
        className="hidden"
        aria-hidden
      />
      <Card
        data-open={open || undefined}
        className="group/collapsible-card sticky top-6 hidden h-[calc(100vh-7rem)] max-h-[800px] min-h-[480px] shrink-0 self-start overflow-visible transition-[width] not-data-open:w-11 data-open:w-[360px] lg:flex"
      >
        <button
          type="button"
          onClick={() => setOpen((b) => !b)}
          aria-label="Collapse analysis details"
          className="absolute top-4 left-0 z-10 flex size-7 -translate-x-1/2 cursor-pointer items-center justify-center rounded-full border border-[#FCCEEE] bg-[#FDF2FA] transition-colors hover:bg-[#FCE7F3] dark:border-[#DD2590]/60 dark:bg-[#DD2590]/40 dark:hover:bg-[#DD2590]/55"
        >
          <ChevronLeft
            className="size-4 text-[#DD2590] transition-transform group-data-open/collapsible-card:rotate-180"
            strokeWidth={2.5}
          />
        </button>
        {!isMobile && open ? (
          <CardContent className="flex h-full flex-col gap-5 overflow-y-auto">
            {content}
          </CardContent>
        ) : (
          <button
            type="button"
            onClick={toggleOpen}
            className="relative hidden h-full min-h-[480px] w-full shrink-0 cursor-pointer flex-col items-center overflow-visible pb-6 shadow-xs transition-colors hover:bg-[#FAFAFA] lg:flex dark:hover:bg-muted/40"
            aria-label="Expand analysis details"
          >
            <span className="py-3 text-sm font-medium text-[#414651] [writing-mode:vertical-rl] dark:text-foreground">
              Analysis Details
            </span>
          </button>
        )}
      </Card>

      <Sheet open={isMobile && open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="p-5">
          {content}
        </SheetContent>
      </Sheet>
    </>
  );
}

function TranscriptWorkspace() {
  const [segmentTab, setSegmentTab] = useState<SegmentTabValue>("transcript");
  const [showSavedOnly, setShowSavedOnly] = useState(false);

  const participant = useParticipant();

  return (
    <Card className="overflow-hidden">
      <CardContent className="flex flex-col gap-4">
        <SegmentTabBar active={segmentTab} onChange={setSegmentTab} />

        {segmentTab === "transcript" && (
          <>
            <div className="flex items-center justify-end gap-2">
              <Label
                htmlFor="show-saved-only"
                className="text-sm font-medium text-[#414651]"
              >
                Show saved only
              </Label>
              <Switch
                id="show-saved-only"
                checked={showSavedOnly}
                onCheckedChange={setShowSavedOnly}
                className="data-[state=checked]:bg-[#DD2590]"
              />
            </div>

            <TranscriptSegments showSavedOnly={showSavedOnly} />
          </>
        )}

        {segmentTab === "pii" && <PiiListTab participant={participant.data} />}
      </CardContent>
    </Card>
  );
}

function DetailsSection() {
  const [selectedUtterance] = useSelectedUtterance();
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (selectedUtterance) setDetailsOpen(true);
  }, [selectedUtterance]);

  return (
    <div className="flex flex-col items-stretch gap-4 lg:flex-row lg:gap-6">
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <TranscriptWorkspace />

        {/* TODO: stick to bottom of screen so that you can actually click on it on mobile */}
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-lg border-[#FCCEEE] bg-[#FDF2FA] text-[#C11574] hover:bg-[#FCE7F3] lg:hidden dark:border-[#DD2590]/40 dark:bg-[#DD2590]/15 dark:text-[#F472D0] dark:hover:bg-[#DD2590]/25"
          onClick={() => setDetailsOpen(true)}
        >
          View Analysis Details
        </Button>
      </div>

      <AnalysisDetailsPanel open={detailsOpen} setOpen={setDetailsOpen} />
    </div>
  );
}

function RouteComponent() {
  const selectedUtteranceState = useState<UtteranceHistoryEntry | null>(null);

  const participant = useParticipant();

  const riskScore = participant.data.analytics?.risk_score.total ?? null;

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-6">
      <div className="flex flex-col gap-3">
        <Breadcrumbs />
        <MeetingMeta />
      </div>
      {/* TODO: put designs from figma here */}
      {riskScore === null ? null : riskScore < 30 ? (
        <SafeCard />
      ) : riskScore < 60 ? (
        <ModerateCard />
      ) : (
        <RiskAlertCard />
      )}

      <StatCards />

      <AnalysisGraph />
      <SelectedUtteranceContext value={selectedUtteranceState}>
        <DetailsSection />
      </SelectedUtteranceContext>

      {/* <ConfirmationDialog
        open={segmentToUnsave !== null}
        onOpenChange={(open) => {
          if (!open) setSegmentToUnsave(null);
        }}
        title="Unsave?"
        description="Unsaving this segment will also permanently delete your attached note."
        onConfirm={handleConfirmUnsave}
        confirmText="Unsave"
        cancelText="Cancel"
        icon={ConfirmationDialogWarningIcon}
        decorative
      /> */}
    </div>
  );
}
